"use client";

import React, { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  styled,
  CircularProgress,
} from "@mui/material";
import ProductRow from "./ProductRow";
import { useAppSelector } from "@/hooks/useAppSelector";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import ArrowDropUpOutlinedIcon from "@mui/icons-material/ArrowDropUpOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { trackEvent } from "@/lib/gtag";
import BrandsAndFilter from "./BrandsAndFilter";
import { shallowEqual } from "react-redux";

const ProductTable: React.FC = () => {
  const { products, loading } = useAppSelector(
    (state) => ({ products: state.home.products, loading: state.home.loading }),
    shallowEqual
  );
  const { selectedFilters } = useAppSelector(
    (state) => state.steps,
    shallowEqual
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    field: "mrp" | "ndp" | null;
    order: "asc" | "desc" | null;
  }>({
    field: null,
    order: null,
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchQuery(searchValue);

    if (searchValue) {
      trackEvent({
        action: "product_search",
        category: "product_table",
        label: searchValue,
      });
    }
  };

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!products) return [];

    return products
      .filter((product) => {
        const matchesSearch =
          product.prod_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.Bar_Code?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesFilters =
          selectedFilters?.length === 0 ||
          selectedFilters?.some(
            (filter) => product.SubcategorieName === filter.SubcategorieName
          );

        return matchesSearch && matchesFilters;
      })
      .sort((a, b) => {
        if (!sortConfig.field || !sortConfig.order) return 0;

        const fieldKey = sortConfig.field === "mrp" ? "MRP_price" : "NDP_price";

        return sortConfig.order === "asc"
          ? Number(a[fieldKey]) - Number(b[fieldKey])
          : Number(b[fieldKey]) - Number(a[fieldKey]);
      });
  }, [products, selectedFilters, searchQuery, sortConfig]);

  const CustomTooltip = styled(Tooltip)({
    "& .MuiTooltip-tooltip": {
      backgroundColor: "white",
      color: "black",
      fontSize: "12px",
      fontWeight: "500",
      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
      borderRadius: "4px",
    },
    "& .MuiTooltip-arrow": {
      color: "white",
    },
  });

  return (
    <>
      <BrandsAndFilter />
      <TableContainer
        component={Paper}
        className=" product-table mb-[100px]"
        sx={{
          maxHeight: "calc(100vh - 230px)",
          overflow: "auto",
          borderRadius: "0",
          boxShadow: "none",
        }}
        elevation={0}
      >
        <Table stickyHeader className="min-w-full min-h-[30vh]">
          {/* Table Header */}
          <TableHead>
            <TableRow style={{ backgroundColor: "#283248", color: "white" }}>
              {[
                "Bar Code",
                "Image",
                "Product Name",
                "Dealer Price (Excluding GST)",
                "MRP",
                "Quantity",
                "Total",
              ].map((header) => (
                <TableCell
                  key={header}
                  className="font-bold"
                  style={{ color: "white", padding: "16px" }}
                  sx={{
                    backgroundColor: "#283248",
                  }}
                  align="center"
                >
                  {header === "Product Name" ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        fontWeight="800"
                        letterSpacing="1px"
                        whiteSpace="nowrap"
                        fontFamily="Raleway"
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                          fontSize: "12px",
                        }}
                      >
                        {header}
                      </Typography>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginLeft: "20px",
                        }}
                      >
                        <SearchIcon
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "8px",
                            transform: "translateY(-50%)",
                            fontSize: "16px",
                            color: "#ccc",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Search for product name"
                          value={searchQuery}
                          onChange={handleSearchChange}
                          style={{
                            padding: "4px 8px 4px 28px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            height: "25px",
                            color: "black",
                            outline: "none",
                            fontSize: "12px",
                            fontFamily: "Raleway",
                          }}
                        />
                      </div>
                    </div>
                  ) : header === "Dealer Price (Excluding GST)" ||
                    header === "MRP" ? (
                    <Typography
                      fontWeight="800"
                      letterSpacing="1px"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      fontFamily="Raleway"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      <span>{header}</span>

                      <span
                        className="flex items-center cursor-pointer ml-1"
                        onClick={() => {
                          const field =
                            header === "Dealer Price (Excluding GST)"
                              ? "ndp"
                              : "mrp";
                          setSortConfig((prev) => ({
                            field,
                            order:
                              prev.field === field && prev.order === "asc"
                                ? "desc"
                                : "asc",
                          }));
                          trackEvent({
                            action: "sort",
                            category: "product_table",
                            label: `Sorted by ${field.toUpperCase()} - ${
                              sortConfig.order === "asc" ? "desc" : "asc"
                            }`,
                          });
                        }}
                      >
                        {sortConfig.field ===
                          (header === "Dealer Price (Excluding GST)"
                            ? "ndp"
                            : "mrp") && sortConfig.order === "asc" ? (
                          <ArrowDropUpOutlinedIcon style={{ color: "white" }} />
                        ) : (
                          <ArrowDropDownOutlinedIcon
                            style={{ color: "white" }}
                          />
                        )}
                      </span>
                    </Typography>
                  ) : header === "Quantity" ? (
                    <Typography
                      fontWeight="800"
                      letterSpacing="1px"
                      // whiteSpace="nowrap"
                      fontFamily="Raleway"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      {header}
                      <CustomTooltip
                        title="Increase or Decrease quantity according to MOQ"
                        arrow
                        placement="right"
                      >
                        <InfoOutlinedIcon
                          style={{
                            fontSize: "15px",
                            marginLeft: "5px",
                            cursor: "pointer",
                          }}
                        />
                      </CustomTooltip>
                    </Typography>
                  ) : (
                    <Typography
                      fontWeight="800"
                      letterSpacing="1px"
                      // whiteSpace="nowrap"
                      fontFamily="Raleway"
                      sx={{
                        fontSize: "12px",
                      }}
                    >
                      {header}
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody sx={{ minHeight: "30vh" }}>
            {loading && (
              <tr>
                <td colSpan={100} className="relative ">
                  <div className="fixed top-[10%] inset-0 flex items-center justify-center">
                    <CircularProgress
                      size={20}
                      sx={{ color: "var(--color-blue)" }}
                    />
                  </div>
                </td>
              </tr>
            )}
            {filteredProducts?.map((data) => (
              <ProductRow key={data.SKU_ID} data={data} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default React.memo(ProductTable);
